import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ReactNode, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ProtectedRoute from './components/ProtectedRoute';
import { isChronicEnabled } from './config/appMode';
import BottomNavigation from './components/layout/BottomNavigation';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import LifestyleModeSelector from './pages/LifestyleModeSelector';
import HealthGoalSelector from './pages/HealthGoalSelector';
import CalorieGoalsSetup from './pages/CalorieGoalsSetup';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import ProfileForm from './pages/ProfileForm';
import ProfileView from './pages/ProfileView';
import MedicationsPage from './pages/MedicationsPage';
import NutritionPage from './pages/NutritionPage';
import MealPlanDetail from './pages/MealPlanDetail';
import FoodPreferencesPage from './pages/FoodPreferencesPage';
import ExercisesPage from './pages/ExercisesPage';
import { ChatPage } from './pages/ChatPage';
import ResearchPage from './pages/ResearchPage';
import SpecialistsPage from './pages/SpecialistsPage';
import SpecialistDetailPage from './pages/SpecialistDetailPage';
import { CalendarPage } from './pages/CalendarPage';
import SpecialistDashboard from './pages/SpecialistDashboard';
import SpecialistProfileEdit from './pages/SpecialistProfileEdit';
import SettingsPage from './pages/SettingsPage';
import HelpPage from './pages/HelpPage';
import FoodDashboard from './pages/FoodDashboard';
import GoalsPage from './pages/GoalsPage';
import ChallengesPage from './pages/ChallengesPage';
import AdvancedTrackingPage from './pages/AdvancedTrackingPage';
import WaterIntakePage from './pages/WaterIntakePage';
import InsightsPage from './pages/InsightsPage';
import EmotionalWellbeingPage from './pages/EmotionalWellbeingPage';
import TherapyChatPage from './pages/TherapyChatPage';
import AdminEducationDashboard from './pages/AdminEducationDashboard';
import AdminArticleEditor from './pages/AdminArticleEditor';
import AdminCommunityDashboard from './pages/AdminCommunityDashboard';
import AdminAIUsage from './pages/AdminAIUsage';
import AdminPushNotifications from './pages/AdminPushNotifications';
import EducationPage from './pages/EducationPage';
import ArticlesListPage from './pages/ArticlesListPage';
import ArticleDetailPage from './pages/ArticleDetailPage';
import EducationFAQsPage from './pages/EducationFAQsPage';
import CommunityHomePage from './pages/community/CommunityHomePage';
import CommunityFeedPage from './pages/community/CommunityFeedPage';
import PostDetailPage from './pages/community/PostDetailPage';
import CreatePostPage from './pages/community/CreatePostPage';
import EditPostPage from './pages/community/EditPostPage';
import ConversationsListPage from './pages/community/ConversationsListPage';
import DirectMessagesPage from './pages/community/DirectMessagesPage';
import FriendsPage from './pages/community/FriendsPage';

import ContactPage from './pages/ContactPage';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SubscriptionsPage from './pages/SubscriptionsPage';
import ExtraCreditsPage from './pages/ExtraCreditsPage';
import ExtraCreditsSuccess from './pages/ExtraCreditsSuccess';
import ExtraCreditsCancel from './pages/ExtraCreditsCancel';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import CareCirclePage from './pages/CareCirclePage';
import MonitorDashboardPage from './pages/MonitorDashboardPage';
import FiiFitBusinessPage from './pages/FiiFitBusinessPage';
import BusinessRegisterPage from './pages/BusinessRegisterPage';
import SpecialistSettings from './pages/SpecialistSettings';
import PrescriptionScannerPage from './pages/PrescriptionScannerPage';
import PrescriptionHistoryPage from './pages/PrescriptionHistoryPage';
import ProgressPhotosPage from './pages/ProgressPhotosPage';
import JournalPage from './pages/JournalPage';
import RewardsPage from './pages/RewardsPage';
import ReferralsPage from './pages/ReferralsPage';
import FoodCommunityPage from './pages/FoodCommunityPage';
import CreateRecipePostPage from './pages/CreateRecipePostPage';
import RecipePostDetailPage from './pages/RecipePostDetailPage';
import RecipeCollectionsPage from './pages/RecipeCollectionsPage';
import FoodCommunitySearchPage from './pages/FoodCommunitySearchPage';
import EditRecipePostPage from './pages/EditRecipePostPage';
import NotificationsPage from './pages/NotificationsPage';
import './index.css';
import { isIOS } from './services/platform.service';

// Global Bottom Navigation - shows on all authenticated patient pages
function GlobalBottomNav() {
  const { user } = useAuth();
  const location = useLocation();
  
  // Pages where bottom nav should NOT show
  const hideOnPaths = ['/', '/login', '/register', '/reset-password', '/lifestyle-mode', '/health-goal', '/calorie-goals-setup'];
  const isPublicPage = hideOnPaths.includes(location.pathname);
  const isAdminPage = location.pathname.startsWith('/admin');
  // Hide bottom nav on individual message conversations (but keep it on the conversations list)
  const isMessageConversation = /^\/community\/messages\/.+/.test(location.pathname);
  const isSpecialistPage = location.pathname.startsWith('/specialist');
  // Hide bottom nav on chat pages (overlaps with chat input)
  const isChatPage = location.pathname === '/chat' || location.pathname === '/therapy-chat';
  
  // Only show for authenticated patients on non-public pages
  if (!user || isPublicPage || isAdminPage || isSpecialistPage || isMessageConversation || isChatPage || user.userType === 'SPECIALIST') {
    return null;
  }
  
  return <BottomNavigation />;
}

/**
 * Wrapper pentru rute moștenite din modul CHRONIC care țin de modul "CHRONIC"
 * (specialiști, prescripții, terapie, cerc de îngrijire, chat medical, etc.).
 *
 * În aplicația FII FIT (APP_MODE = 'FITNESS') aceste rute sunt ascunse —
 * orice navigare către ele redirecționează utilizatorul la `/dashboard`.
 * Paginile NU sunt șterse din baza de cod și se vor reactiva automat dacă
 * se comută `APP_MODE` în `config/appMode.ts`.
 */
// Referral invite redirect - stores code in sessionStorage then goes to /register
function ReferralRedirect() {
  const { code } = useParams<{ code: string }>();
  useEffect(() => {
    if (code) {
      sessionStorage.setItem('referralCode', code.toUpperCase());
    }
  }, [code]);
  return <Navigate to="/register" replace />;
}

function ChronicOnlyRoute({ children }: { children: ReactNode }) {
  if (!isChronicEnabled()) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
        <Toaster
          position="top-center"
          containerStyle={{
            top: 'max(env(safe-area-inset-top), 16px)',
          }}
          toastOptions={{
            style: {
              marginTop: '4px',
            },
          }}
        />
        <Routes>
          {/* Home Page */}
          <Route path="/" element={<Home />} />

          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Lifestyle Mode Selection */}
          <Route 
            path="/lifestyle-mode" 
            element={
              <ProtectedRoute>
                <LifestyleModeSelector />
              </ProtectedRoute>
            } 
          />

          {/* Health Goal Selection */}
          <Route 
            path="/health-goal" 
            element={
              <ProtectedRoute>
                <HealthGoalSelector />
              </ProtectedRoute>
            } 
          />

          {/* Calorie Goals Setup */}
          <Route 
            path="/calorie-goals-setup" 
            element={
              <ProtectedRoute>
                <CalorieGoalsSetup />
              </ProtectedRoute>
            } 
          />

          {/* Protected Routes */}
          <Route 
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/education" 
            element={
              <ProtectedRoute>
                <AdminEducationDashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/education/article/:id" 
            element={
              <ProtectedRoute>
                <AdminArticleEditor />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/community" 
            element={
              <ProtectedRoute>
                <AdminCommunityDashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/ai-usage" 
            element={
              <ProtectedRoute>
                <AdminAIUsage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/push-notifications" 
            element={
              <ProtectedRoute>
                <AdminPushNotifications />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfileView />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/profile/edit" 
            element={
              <ProtectedRoute>
                <ProfileForm />
              </ProtectedRoute>
            } 
          />

          {/* CHRONIC-only: Medicații (rețete, doze, alarme) */}
          <Route 
            path="/profile/medications" 
            element={
              <ProtectedRoute>
                <ChronicOnlyRoute>
                  <MedicationsPage />
                </ChronicOnlyRoute>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/nutrition" 
            element={
              <ProtectedRoute>
                <NutritionPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/nutrition/plan/:id" 
            element={
              <ProtectedRoute>
                <MealPlanDetail />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/nutrition/preferences" 
            element={
              <ProtectedRoute>
                <FoodPreferencesPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/exercises" 
            element={
              <ProtectedRoute>
                <ExercisesPage />
              </ProtectedRoute>
            } 
          />

          {/* CHRONIC-only: Chat AI medical pentru afecțiuni cronice */}
          <Route 
            path="/chat"
            element={
              <ProtectedRoute>
                {/* FII FIT: Nutriționist AI disponibil tuturor (fără gard CHRONIC). */}
                <ChatPage />
              </ProtectedRoute>

            } 
          />

          {/* CHRONIC-only: Cercetare medicală pe boli cronice */}
          <Route 
            path="/research" 
            element={
              <ProtectedRoute>
                <ChronicOnlyRoute>
                  <ResearchPage />
                </ChronicOnlyRoute>
              </ProtectedRoute>
            } 
          />

          {/* Specialiști Fitness — accesibil tuturor utilizatorilor autentificați */}
          <Route 
            path="/specialists" 
            element={
              <ProtectedRoute>
                <SpecialistsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/specialists/:id" 
            element={
              <ProtectedRoute>
                <SpecialistDetailPage />
              </ProtectedRoute>
            } 
          />

          {/* CHRONIC-only: Calendar programări medicale */}
          <Route 
            path="/calendar" 
            element={
              <ProtectedRoute>
                <ChronicOnlyRoute>
                  <CalendarPage />
                </ChronicOnlyRoute>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/food-tracking" 
            element={
              <ProtectedRoute>
                <FoodDashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/goals" 
            element={
              <ProtectedRoute>
                <GoalsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/challenges" 
            element={
              <ProtectedRoute>
                <ChallengesPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/advanced-tracking" 
            element={
              <ProtectedRoute>
                <AdvancedTrackingPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/water-intake" 
            element={
              <ProtectedRoute>
                <WaterIntakePage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/insights" 
            element={
              <ProtectedRoute>
                <InsightsPage />
              </ProtectedRoute>
            } 
          />

          {/* CHRONIC-only: Wellbeing emoțional pentru pacienți cronici */}
          <Route 
            path="/emotional-wellbeing" 
            element={
              <ProtectedRoute>
                <ChronicOnlyRoute>
                  <EmotionalWellbeingPage />
                </ChronicOnlyRoute>
              </ProtectedRoute>
            } 
          />

          {/* CHRONIC-only: Chat de terapie */}
          <Route 
            path="/therapy-chat" 
            element={
              <ProtectedRoute>
                <ChronicOnlyRoute>
                  <TherapyChatPage />
                </ChronicOnlyRoute>
              </ProtectedRoute>
            } 
          />

          {/* Specialist Business Routes — accesibile în FII FIT */}
          <Route 
            path="/specialist/dashboard" 
            element={
              <ProtectedRoute>
                <SpecialistDashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/specialist/profile/edit" 
            element={
              <ProtectedRoute>
                <SpecialistProfileEdit />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/specialist/settings" 
            element={
              <ProtectedRoute>
                <SpecialistSettings />
              </ProtectedRoute>
            } 
          />

          {/* Settings Route (Common for all users) */}
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } 
          />

          {/* Subscription & Credits Routes */}
          <Route 
            path="/subscriptions" 
            element={
              <ProtectedRoute>
                <SubscriptionsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/extra-credits" 
            element={
              <ProtectedRoute>
                <ExtraCreditsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/extra-credits/success" 
            element={
              <ProtectedRoute>
                <ExtraCreditsSuccess />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/extra-credits/cancel" 
            element={
              <ProtectedRoute>
                <ExtraCreditsCancel />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/subscriptions/success" 
            element={
              <ProtectedRoute>
                <SubscriptionSuccess />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/subscriptions/cancel" 
            element={
              <ProtectedRoute>
                <SubscriptionCancel />
              </ProtectedRoute>
            } 
          />

          {/* Help/Contact Route (Common for all users) */}
          <Route 
            path="/help" 
            element={
              <ProtectedRoute>
                <HelpPage />
              </ProtectedRoute>
            } 
          />

          {/* Education Routes (Public for users) */}
          <Route 
            path="/education" 
            element={
              <ProtectedRoute>
                <EducationPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/education/articles" 
            element={
              <ProtectedRoute>
                <ArticlesListPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/education/article/:slug" 
            element={
              <ProtectedRoute>
                <ArticleDetailPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/education/faqs" 
            element={
              <ProtectedRoute>
                <EducationFAQsPage />
              </ProtectedRoute>
            } 
          />

          {/* Public Pages - Contact, Terms, Privacy */}
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* FII FIT Business — Public landing & registration */}
          <Route path="/business" element={isIOS() ? <Navigate to="/login" replace /> : <FiiFitBusinessPage />} />
          <Route path="/business/register" element={isIOS() ? <Navigate to="/login" replace /> : <BusinessRegisterPage />} />

          {/* Community Routes */}
          <Route 
            path="/community" 
            element={
              <ProtectedRoute>
                <CommunityHomePage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/community/friends" 
            element={
              <ProtectedRoute>
                <FriendsPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/community/:diseaseCode" 
            element={

              <ProtectedRoute>
                <CommunityFeedPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/community/posts/:postId" 
            element={
              <ProtectedRoute>
                <PostDetailPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/community/posts/:postId/edit" 
            element={
              <ProtectedRoute>
                <EditPostPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/community/create-post" 
            element={
              <ProtectedRoute>
                <CreatePostPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/community/messages" 
            element={
              <ProtectedRoute>
                <ConversationsListPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/community/messages/:userId" 
            element={
              <ProtectedRoute>
                <DirectMessagesPage />
              </ProtectedRoute>
            } 
          />

          {/* CHRONIC-only: Care Circle - Cercul de îngrijire al pacientului */}
          <Route 
            path="/care-circle" 
            element={
              <ProtectedRoute>
                <ChronicOnlyRoute>
                  <CareCirclePage />
                </ChronicOnlyRoute>
              </ProtectedRoute>
            } 
          />

          {/* CHRONIC-only: Monitor Dashboard - Public (token-based) */}
          <Route
            path="/monitor/:token"
            element={
              <ChronicOnlyRoute>
                <MonitorDashboardPage />
              </ChronicOnlyRoute>
            }
          />

          {/* Progress Photos - Fitness Module */}
          <Route
            path="/progress-photos"
            element={
              <ProtectedRoute>
                <ProgressPhotosPage />
              </ProtectedRoute>
            }
          />

          {/* Journal - Tranzit intestinal + Notițe zilnice (Fitness Wellness) */}
          <Route
            path="/journal"
            element={
              <ProtectedRoute>
                <JournalPage />
              </ProtectedRoute>
            }
          />

          {/* CHRONIC-only: Scanner & istoric rețete */}
          <Route
            path="/prescription-scanner"
            element={
              <ProtectedRoute>
                <ChronicOnlyRoute>
                  <PrescriptionScannerPage />
                </ChronicOnlyRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/prescription-history"
            element={
              <ProtectedRoute>
                <ChronicOnlyRoute>
                  <PrescriptionHistoryPage />
                </ChronicOnlyRoute>
              </ProtectedRoute>
            }
          />

          {/* Rewards & Referrals */}
          <Route
            path="/rewards"
            element={
              <ProtectedRoute>
                <RewardsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/referrals"
            element={
              <ProtectedRoute>
                <ReferralsPage />
              </ProtectedRoute>
            }
          />

          {/* Public referral invite link - redirects to register with code pre-filled */}
          <Route
            path="/invita/:code"
            element={<ReferralRedirect />}
          />

          {/* Food Community Routes */}
          <Route
            path="/food-community"
            element={
              <ProtectedRoute>
                <FoodCommunityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/food-community/create"
            element={
              <ProtectedRoute>
                <CreateRecipePostPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/food-community/post/:postId"
            element={
              <ProtectedRoute>
                <RecipePostDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/food-community/collections"
            element={
              <ProtectedRoute>
                <RecipeCollectionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/food-community/search"
            element={
              <ProtectedRoute>
                <FoodCommunitySearchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/food-community/post/:postId/edit"
            element={
              <ProtectedRoute>
                <EditRecipePostPage />
              </ProtectedRoute>
            }
          />

          {/* Notifications Route */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />

          {/* 404 - Redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        {/* Global Bottom Navigation - visible on all authenticated patient pages */}
        <GlobalBottomNav />
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
